import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import { Contact, TeamWithMembers } from '../model';
import { selectTeams } from '../store/helpers';
import { useSelector } from '../store/utils';
import ContactPhoto from './ContactPhoto';
import css from './TeamsDropdown.module.scss';

type TeamsDropdown = {
   className?: string
   right?: boolean
   onSelect: (team: TeamWithMembers) => void
}

export default function TeamsDropdown({ className, onSelect, right, children }: React.PropsWithChildren<TeamsDropdown>) {
   const teams = useSelector(selectTeams)
   const [isOpen, setOpen] = useState(false)
   const [value, setValue] = useState('')
   const [filteredTeams, setFilteredTeams] = useState<TeamWithMembers[]>([])

   const canOpen = useRef(true)
   const openTimeout = useRef<number>()

   useEffect(() => {
      if (isOpen && teams && value.trim()) {
         setFilteredTeams(teams.filter(t => filterTitle(t.displayName, value)))
      } else {
         setFilteredTeams([])
      }
   }, [value, isOpen, teams])

   function renderTeam(team: TeamWithMembers, isHighlighted: boolean) {
      return <div key={team.id} className={classNames(css.team, isHighlighted && css.active)}>
         <div className={css.title}>
            {team.displayName}
         </div>
         <div className={css.members}>
            {renderMembers(team)}
         </div>
      </div>
   }

   function handleSelect(team: TeamWithMembers) {
      setOpen(false)
      onSelect(team)
   }

   function onBlur() {
      window.clearTimeout(openTimeout.current)
      canOpen.current = false
      setOpen(false)
      openTimeout.current = window.setTimeout(() => canOpen.current = true, 200)
   }

   const menuClass = classNames(css.autoComplete, right && css.right)

   return <div className={classNames(css.teamsDropdown, className)} onBlur={() => teams === null && onBlur()}>
      <button className={css.toggle} onClick={() => canOpen.current && setOpen(!isOpen)}>
         {children}
      </button>
      {isOpen && (teams === null ? <div className={menuClass}>Loading...</div> : <div className={menuClass} style={{
         position: 'relative',
         backgroundColor: 'white',
         border: '1px solid #ccc',
         maxHeight: '400px',
         overflowY: 'auto'
      }}>
         <input 
            placeholder='Type to search...' 
            autoFocus 
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onBlur={onBlur}
            style={{
               width: '100%',
               padding: '8px',
               borderBottom: '1px solid #ccc',
               boxSizing: 'border-box'
            }}
         />
         <div className={css.menu}>
            {filteredTeams.length > 0 ? filteredTeams.map((team, idx) => (
               <div 
                  key={`${team.id}-${idx}`}
                  onClick={() => handleSelect(team)}
                  role="button"
                  tabIndex={0}
                  style={{ cursor: 'pointer', padding: '8px', borderBottom: '1px solid #eee' }}
               >
                  {renderTeam(team, false)}
               </div>
            )) : teams && teams.length === 0 && <div style={{ padding: '8px' }}>No teams found</div>}
         </div>
      </div>)}
   </div>
}

export function renderMembers(team: TeamWithMembers) {
   if (team.members.length <= 3) {
      return team.members.map(renderMember)
   }

   const rest = team.members.filter((_, i) => i >= 2).map(m => m.name)

   return <>
      {team.members.filter((_, i) => i < 2).map(renderMember)}
      <div className={classNames(css.member, css.moreMembers)} title={rest.join(', ')}
>
         +{rest.length}
      </div>
   </>
}

function renderMember(member: Contact) {
   return <div key={member.emailAddress} className={css.member}>
      <ContactPhoto contact={member} />
      <div className={css.name}>{member.name}</div>
   </div>
}

function filterTitle(title: string, text: string) {
   return title.toLowerCase().includes(text.toLowerCase())
}
